/**
 * Driver Financial Privacy — UI: earnings reveal gate + unlock dialog + a
 * "financial PIN" settings block for the Account tab.
 *
 * Display/privacy only. Wrap the earnings dashboard with <DriverEarningsGate>;
 * it shows a lock screen until the driver reveals with their PIN (or account
 * password), then renders the existing dashboard unchanged.
 */
import React, { useState } from "react";
import { Lock, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useDriverFinancialPrivacy } from "../../lib/driverFinancialPrivacy";
import { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const UnlockDialog = ({ open, onClose }) => {
  const { hasPin, unlock } = useDriverFinancialPrivacy();
  const [credential, setCredential] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!credential) return;
    setBusy(true);
    setError(null);
    try {
      await unlock(credential);
      setCredential("");
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "object" && detail?.message ? detail.message : formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="driver-earnings-unlock-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white border border-border rounded-2xl p-5 w-full max-w-sm space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
            <Lock className="w-4 h-4 text-[#002FA7]" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-black leading-tight">Show earnings</h3>
            <p className="label-mini text-muted-foreground">
              {hasPin ? "Enter your financial PIN" : "Enter your account password"}
            </p>
          </div>
        </div>
        <Input
          type="password"
          inputMode={hasPin ? "numeric" : "text"}
          autoComplete="off"
          autoFocus
          value={credential}
          onChange={(e) => setCredential(e.target.value)}
          placeholder={hasPin ? "••••" : "Password"}
          className="font-mono-tab"
          data-testid="driver-earnings-unlock-input"
        />
        {error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="driver-earnings-unlock-error">
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose} data-testid="driver-earnings-unlock-cancel">
            Cancel
          </Button>
          <Button type="submit" className="flex-1" disabled={busy || !credential} data-testid="driver-earnings-unlock-submit">
            {busy ? "Checking…" : "Reveal"}
          </Button>
        </div>
      </form>
    </div>
  );
};

/** Gate the earnings dashboard. Locked → privacy screen; revealed → children. */
export const DriverEarningsGate = ({ children }) => {
  const { revealed, hide } = useDriverFinancialPrivacy();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Re-hide earnings the moment the driver leaves the Earnings view (unmount).
  React.useEffect(() => () => hide(), [hide]);

  if (revealed) {
    return (
      <div data-testid="driver-earnings-revealed" className="space-y-4">
        <button
          type="button"
          onClick={hide}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-border bg-white hover:bg-secondary transition-colors"
          data-testid="driver-earnings-hide-btn"
        >
          <EyeOff className="w-3.5 h-3.5" /> Hide earnings
        </button>
        {children}
      </div>
    );
  }

  return (
    <>
      <div
        className="bg-white border border-border rounded-2xl p-8 flex flex-col items-center text-center gap-3"
        data-testid="driver-earnings-lock-screen"
      >
        <span className="w-14 h-14 rounded-full bg-[#0A0A0A] flex items-center justify-center">
          <Lock className="w-6 h-6 text-white" />
        </span>
        <div>
          <h3 className="font-heading font-black text-lg">Earnings hidden</h3>
          <p className="text-sm text-muted-foreground max-w-xs mt-1">
            Your earnings are kept private. Reveal them with your PIN or account password.
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="mt-1 h-11 px-6 font-bold"
          data-testid="driver-earnings-reveal-btn"
        >
          <Eye className="w-4 h-4 mr-2" /> Reveal earnings
        </Button>
      </div>
      <UnlockDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

/** Account-tab block: set / change / remove the financial PIN. */
export const DriverPinSettings = () => {
  const { hasPin, setPin, clearPin } = useDriverFinancialPrivacy();
  const [mode, setMode] = useState(null); // "set" | "clear" | null
  const [password, setPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => { setMode(null); setPassword(""); setNewPin(""); setError(null); };

  const submitSet = async (e) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(newPin)) { setError("PIN must be 4–6 digits."); return; }
    setBusy(true); setError(null);
    try {
      await setPin(password, newPin);
      toast.success("Financial PIN saved.");
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "object" && detail?.message ? detail.message : formatApiError(err));
    } finally { setBusy(false); }
  };

  const submitClear = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await clearPin(password);
      toast.success("Financial PIN removed. Earnings now reveal with your account password.");
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "object" && detail?.message ? detail.message : formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border p-4" data-testid="driver-pin-settings">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-[#002FA7]" />
        <div className="label-mini text-muted-foreground">FINANCIAL PRIVACY PIN</div>
      </div>
      <div className="flex items-center gap-2 text-sm mb-3">
        {hasPin ? (
          <span className="inline-flex items-center gap-1 text-[#16A34A] font-semibold" data-testid="driver-pin-status">
            <ShieldCheck className="w-4 h-4" /> PIN set — earnings reveal with your PIN
          </span>
        ) : (
          <span className="text-muted-foreground" data-testid="driver-pin-status">
            No PIN set — earnings reveal with your account password
          </span>
        )}
      </div>

      {mode === null && (
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 text-xs font-bold" onClick={() => setMode("set")} data-testid="driver-pin-set-btn">
            {hasPin ? "Change PIN" : "Set a PIN"}
          </Button>
          {hasPin && (
            <Button variant="outline" className="flex-1 text-xs font-bold" onClick={() => setMode("clear")} data-testid="driver-pin-clear-btn">
              Remove PIN
            </Button>
          )}
        </div>
      )}

      {mode === "set" && (
        <form onSubmit={submitSet} className="space-y-2" data-testid="driver-pin-set-form">
          <Input type="password" inputMode="numeric" autoComplete="off" value={newPin} maxLength={6}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
            placeholder="New PIN (4–6 digits)" className="font-mono-tab" data-testid="driver-pin-new-input" />
          <Input type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Account password" data-testid="driver-pin-password-input" />
          {error && <div className="text-xs text-destructive" data-testid="driver-pin-error">{error}</div>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={reset} data-testid="driver-pin-cancel">Cancel</Button>
            <Button type="submit" className="flex-1" disabled={busy || !newPin || !password} data-testid="driver-pin-save">
              {busy ? "Saving…" : "Save PIN"}
            </Button>
          </div>
        </form>
      )}

      {mode === "clear" && (
        <form onSubmit={submitClear} className="space-y-2" data-testid="driver-pin-clear-form">
          <Input type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Account password" data-testid="driver-pin-clear-password-input" />
          {error && <div className="text-xs text-destructive" data-testid="driver-pin-error">{error}</div>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={reset} data-testid="driver-pin-clear-cancel">Cancel</Button>
            <Button type="submit" variant="destructive" className="flex-1" disabled={busy || !password} data-testid="driver-pin-clear-confirm">
              {busy ? "Removing…" : "Remove PIN"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
